/**
 * Authentication service for admin users
 */

// Token storage key in localStorage
const AUTH_TOKEN_KEY = 'dekalio_admin_auth_token';
const AUTH_EXPIRY_KEY = 'dekalio_admin_auth_expiry';

/**
 * Login admin user
 * @param {string} username - Admin username
 * @param {string} password - Admin password
 * @returns {Promise<Object>} - Auth response with token
 */
export const loginAdmin = async (username, password) => {
  try {
    console.log('Attempting login with:', { username });
    
    const response = await fetch('/.netlify/functions/admin-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Authentication failed');
    }

    const data = await response.json();
    console.log('Login successful, token received', { tokenLength: data.token?.length });
    
    // Store token and expiration in localStorage
    localStorage.setItem(AUTH_TOKEN_KEY, data.token);
    
    // Calculate and store expiry timestamp
    const expiryMs = Date.now() + (data.expiresIn * 1000);
    localStorage.setItem(AUTH_EXPIRY_KEY, expiryMs.toString());
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout admin user
 */
export const logoutAdmin = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_EXPIRY_KEY);
  console.log('User logged out, auth tokens removed');
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} - True if authenticated
 */
export const isAuthenticated = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    
    if (!token) {
      console.log('No auth token found in localStorage');
      return false;
    }
    
    // Check if token is expired based on stored expiry time
    const expiryStr = localStorage.getItem(AUTH_EXPIRY_KEY);
    if (expiryStr) {
      const expiryTime = parseInt(expiryStr, 10);
      
      if (Date.now() >= expiryTime) {
        // Token has expired, clear it
        console.log('Token expired, clearing authentication');
        logoutAdmin();
        return false;
      }
      
      console.log('Token valid until', new Date(expiryTime).toLocaleString());
    }
    
    // Verify token with server
    console.log('Verifying token with server');
    const response = await fetch('/.netlify/functions/admin-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        action: 'verify',
        token
      })
    });

    if (!response.ok) {
      console.log('Server rejected token:', response.status);
      return false;
    }

    const data = await response.json();
    console.log('Token verification response:', data);
    return data.valid === true;
  } catch (error) {
    console.error('Authentication check error:', error);
    return false;
  }
};

// Alias for backward compatibility
export const isLoggedIn = isAuthenticated;

/**
 * Get the authentication token
 * @returns {string|null} - The JWT token or null if not authenticated
 */
export const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Add authentication header to fetch options
 * @param {Object} options - Fetch options
 * @returns {Object} - Options with auth header
 */
export const withAuth = (options = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    console.warn('No auth token available for API request');
    return options;
  }
  
  console.log('Adding auth token to request headers');
  return {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    }
  };
};

// Get admin key for S3 proxy requests
export const getAdminKey = async () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) {
    throw new Error('No admin token found');
  }

  try {
    const response = await fetch('/.netlify/functions/admin-auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getAdminKey',
        token: token
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get admin key');
    }

    const data = await response.json();
    return data.adminKey;
  } catch (error) {
    console.error('Error getting admin key:', error);
    throw error;
  }
}; 